/**
 * Twitch chat badges that ship with the Console.
 *
 * ============================ WHY A STATIC MAP ============================
 * A badge is `{ id, version }` on the event, and the image lives at
 * `/badges/<id>/<version>.png`. Rendering that path blind would produce a
 * broken image for every badge Connaro does not ship — and Twitch publishes
 * 365 badge sets, most of them promotional, so "does not ship" is the common
 * case rather than the exception.
 *
 * Checking the map first means an unknown badge falls back to its text label,
 * which is what the row showed before images existed. Nothing regresses; the
 * known ones simply get better.
 *
 * The value is the badge's TITLE from Twitch's API, used as the image's
 * accessible name. Replacing a text pill with a picture would otherwise delete
 * the information for anyone not looking at the pixels.
 *
 * GENERATED from `GET https://api.twitch.tv/helix/chat/badges/global`, and
 * committed rather than fetched at build time so a build never depends on
 * Twitch being reachable. Regenerate when the shipped assets change.
 * ========================================================================
 */
export const TWITCH_BADGES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  admin: { '1': 'Admin' },
  ambassador: { '1': 'Twitch Ambassador' },
  'artist-badge': { '1': 'Artist' },
  bits: {
    '1': 'cheer 1',
    '100': 'cheer 100',
    '1000': 'cheer 1000',
    '10000': 'cheer 10000',
    '100000': 'cheer 100000',
    '1000000': 'cheer 1000000',
    '1250000': 'cheer 1250000',
    '1500000': 'cheer 1500000',
    '1750000': 'cheer 1750000',
    '200000': 'cheer 200000',
    '2000000': 'cheer 2000000',
    '25000': 'cheer 25000',
    '2500000': 'cheer 2500000',
    '300000': 'cheer 300000',
    '3000000': 'cheer 3000000',
    '3500000': 'cheer 3500000',
    '400000': 'cheer 400000',
    '4000000': 'cheer 4000000',
    '4500000': 'cheer 4500000',
    '5000': 'cheer 5000',
    '50000': 'cheer 50000',
    '500000': 'cheer 500000',
    '5000000': 'cheer 5000000',
    '600000': 'cheer 600000',
    '700000': 'cheer 700000',
    '75000': 'cheer 75000',
    '800000': 'cheer 800000',
    '900000': 'cheer 900000',
  },
  'bits-leader': { '1': 'Bits Leader 1', '2': 'Bits Leader 2', '3': 'Bits Leader 3' },
  broadcaster: { '1': 'Broadcaster' },
  founder: { '0': 'Founder' },
  'game-developer': { '1': 'Game Developer' },
  global_mod: { '1': 'Global Moderator' },
  'hype-train': { '1': 'Current Hype Train Conductor', '2': 'Former Hype Train Conductor' },
  moderator: { '1': 'Moderator' },
  moments: {
    '1': 'Moments Badge - Tier 1',
    '10': 'Moments Badge - Tier 10',
    '11': 'Moments Badge - Tier 11',
    '12': 'Moments Badge - Tier 12',
    '13': 'Moments Badge - Tier 13',
    '14': 'Moments Badge - Tier 14',
    '15': 'Moments Badge - Tier 15',
    '16': 'Moments Badge - Tier 16',
    '17': 'Moments Badge - Tier 17',
    '18': 'Moments Badge - Tier 18',
    '19': 'Moments Badge - Tier 19',
    '2': 'Moments Badge - Tier 2',
    '20': 'Moments Badge - Tier 20',
    '3': 'Moments Badge - Tier 3',
    '4': 'Moments Badge - Tier 4',
    '5': 'Moments Badge - Tier 5',
    '6': 'Moments Badge - Tier 6',
    '7': 'Moments Badge - Tier 7',
    '8': 'Moments Badge - Tier 8',
    '9': 'Moments Badge - Tier 9',
  },
  no_audio: { '1': 'Watching without audio' },
  no_video: { '1': 'Listening only' },
  partner: { '1': 'Verified' },
  predictions: {
    'blue-1': 'Predicted Blue (1)',
    'blue-10': 'Predicted Blue (10)',
    'blue-2': 'Predicted Blue (2)',
    'blue-3': 'Predicted Blue (3)',
    'blue-4': 'Predicted Blue (4)',
    'blue-5': 'Predicted Blue (5)',
    'blue-6': 'Predicted Blue (6)',
    'blue-7': 'Predicted Blue (7)',
    'blue-8': 'Predicted Blue (8)',
    'blue-9': 'Predicted Blue (9)',
    'gray-1': 'Predicted Gray (1)',
    'gray-2': 'Predicted Gray (2)',
    'pink-1': 'Predicted Pink (1)',
    'pink-2': 'Predicted Pink (2)',
  },
  premium: { '1': 'Prime Gaming' },
  staff: { '1': 'Staff' },
  'sub-gift-leader': { '1': 'Gifter Leader 1', '2': 'Gifter Leader 2', '3': 'Gifter Leader 3' },
  'sub-gifter': {
    '1': 'Sub Gifter',
    '10': '10 Gift Subs',
    '100': '100 Gift Subs',
    '1000': '1000 Gift Subs',
    '150': '150 Gift Subs',
    '200': '200 Gift Subs',
    '2000': '2000 Gift Subs',
    '25': '25 Gift Subs',
    '250': '250 Gift Subs',
    '300': '300 Gift Subs',
    '3000': '3000 Gift Subs',
    '350': '350 Gift Subs',
    '400': '400 Gift Subs',
    '4000': '4000 Gift Subs',
    '450': '450 Gift Subs',
    '5': '5 Gift Subs',
    '50': '50 Gift Subs',
    '500': '500 Gift Subs',
    '5000': '5000 Gift Subs',
    '550': '550 Gift Subs',
    '600': '600 Gift Subs',
    '650': '650 Gift Subs',
    '700': '700 Gift Subs',
    '750': '750 Gift Subs',
    '800': '800 Gift Subs',
    '850': '850 Gift Subs',
    '900': '900 Gift Subs',
    '950': '950 Gift Subs',
  },
  subscriber: {
    '0': 'Subscriber',
    '1': 'Subscriber',
    '2': '2-Month Subscriber',
    '3': '3-Month Subscriber',
    '4': '6-Month Subscriber',
    '5': '9-Month Subscriber',
    '6': '6-Month Subscriber',
  },
  turbo: { '1': 'Turbo' },
  twitchbot: { '1': 'AutoMod', '2': 'AutoMod' },
  vip: { '1': 'VIP' },
};

/** The badge's title, or undefined when Connaro ships no image for it. */
export function twitchBadgeTitle(id: string, version: string | undefined): string | undefined {
  // A badge with no version is `1` on Twitch's side; the event omits it when
  // the provider sent an empty string.
  return TWITCH_BADGES[id]?.[version ?? '1'];
}

/**
 * The set's own name, for a badge whose VERSION Connaro does not ship.
 *
 * Subscriber and bits badges are largely CHANNEL-specific — a 12-month
 * subscriber badge belongs to one broadcaster and is not in the global set at
 * all — so this is the common path for them, not an edge case.
 *
 * It returns a name, never a different image. Drawing the generic subscriber
 * badge for a 12-month one would assert a tier the viewer does not have, which
 * is worse than showing the word.
 */
export function twitchBadgeSetTitle(id: string): string | undefined {
  const versions = TWITCH_BADGES[id];
  if (versions === undefined) return undefined;

  // Every version of a set carries the set's name as its title.
  return Object.values(versions)[0];
}
